import { useCallback, useEffect, useMemo, useState } from 'react';
import { NoAddedPage } from './no-page-added';
import { PageCard } from './page-card';

import { Page } from 'types/api/site';
import { DataStateOverlay } from 'ui/components/DataLoadOverlay';
import { getHostAndPath, normalizeUrl } from 'utils/proxyUtils';
import { PageFooter } from './page-footer';

interface Props {
  siteId: string;
  pages?: Page[];
  isLoading: boolean;
  isSuccess: boolean;
}

export const Pages = ({ siteId, pages, isLoading, isSuccess }: Props) => {
  const [addPageEnabled, setAddPageEnabled] = useState(false);

  const currentUrl = useMemo(() => getHostAndPath(normalizeUrl(window.location.href))?.fullPath, []);

  const isCurrentPage = useCallback((page: Page): boolean => {
    const iframeUrl = getHostAndPath(normalizeUrl(page.iframe_url))?.fullPath;
    const targetUrl = getHostAndPath(normalizeUrl(page.target_url))?.fullPath;

    if (iframeUrl && targetUrl) {
      return currentUrl === iframeUrl || currentUrl === targetUrl;
    }

    return false;
  }, [currentUrl]);

  useEffect(() => {
    if (!pages?.length) {
      setAddPageEnabled(true);
      return;
    }

    // Check if there are no matching pages
    const noMatchingPages = pages.every(page => !isCurrentPage(page));
    setAddPageEnabled(noMatchingPages);
  }, [pages, currentUrl, isCurrentPage]);

  const showNoPages = !isLoading && isSuccess && pages?.length === 0;

  return (
    <div className="flex w-full flex-col gap-2">
      <DataStateOverlay
        visible={isLoading}
        text="Loading Pages..."
      />
      <div className="flex flex-col gap-2 overflow-y-auto max-h-[500px]">
        {pages?.map((page) => (
          <PageCard key={page.id || page.url} page={page} />
        ))}
        {showNoPages && <NoAddedPage />}
      </div>
      <PageFooter addEnabled={addPageEnabled} siteId={siteId} />
    </div>
  );
};